import React, { useState, useEffect, useContext, useMemo } from "react";

import LoadingContext from "../../context/Loading/Loading";

import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import InputGroup from "react-bootstrap/InputGroup";

import AuthService from "../../utilities/services/auth.service";
import UserContext from "../../context/User/User";
import { ORGANIZATION_PERMISSION_SLUGS } from "@blocktickets/shared-utils";
import { checkPermission } from "../../utilities/helpers";
import {
  getCustomFees,
  createCustomFee,
  updateCustomFee,
  deleteCustomFee,
} from "../../utilities/api";

import { LoadingContainer } from "../LoadingContainer";
import { PageLoadingContainer } from "../PageLoadingContainer";
import { NoPermissionsContainer } from "../NoPermissionsContainer";

import "../BuyerTypesWrapper/buyerTypesWrapper.scss";
import "./customFeesWrapper.scss";

function formatFeeAmount(a) {
  if (a.option === "percent") {
    return `${Number(a.feeAmount ?? 0)}%`;
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
  }).format(Number(a.feeAmount ?? 0));
}

function PencilIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function CustomFeesWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext);
  const { orgPermissions } = useContext(UserContext);
  const { getPermissions } = AuthService;

  const organization = AuthService.getOrg()[0];
  const orgUUID = organization?.uuid;

  const orgPermissionsReady = orgPermissions?.length > 0;
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.MANAGE_CUSTOM_FEES,
    );

  const [fees, setFees] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    option: "amount",
    feeAmount: "",
  });

  const load = () => {
    setIsLoadingData(true);
    getCustomFees(orgUUID)
      .then((res) => setFees(res?.data?.data ?? []))
      .catch((err) => console.error(err))
      .finally(() => {
        setIsLoadingData(false);
        hideLoading();
      });
  };

  useEffect(() => {
    if (!orgPermissionsReady) return;
    if (!hasPermission) {
      hideLoading();
      return;
    }
    if (!orgUUID) return;
    showLoading();
    load();
  }, [orgPermissionsReady, hasPermission, orgUUID]);

  const sortedFees = useMemo(() => {
    return [...fees].sort((x, y) => {
      const ax = x.attributes ?? x;
      const ay = y.attributes ?? y;
      const nx = Number(ax.feeAmount ?? 0);
      const ny = Number(ay.feeAmount ?? 0);
      if (nx !== ny) return nx - ny;
      return String(ax.name ?? "").localeCompare(
        String(ay.name ?? ""),
        undefined,
        {
          sensitivity: "base",
        },
      );
    });
  }, [fees]);

  const openAddModal = () => {
    setEditingId(null);
    setForm({ name: "", option: "amount", feeAmount: "" });
    setShowModal(true);
  };

  const openEditModal = (row) => {
    const a = row.attributes ?? row;
    setEditingId(row.id);
    setForm({
      name: a.name ?? "",
      option: a.option === "percent" ? "percent" : "amount",
      feeAmount:
        a.feeAmount !== undefined && a.feeAmount !== null
          ? String(a.feeAmount)
          : "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (!saving) {
      setShowModal(false);
      setEditingId(null);
    }
  };

  const feeNameMeetsMinLength = form.name.trim().length >= 3;
  const feeAmountTrimmed = String(form.feeAmount ?? "").trim();
  const feeAmountNum = Number(feeAmountTrimmed);
  const feeAmountProvided =
    feeAmountTrimmed !== "" &&
    !Number.isNaN(feeAmountNum) &&
    Number.isFinite(feeAmountNum);
  const canSubmitFee = feeNameMeetsMinLength && feeAmountProvided;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!orgUUID || !canSubmitFee) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      option: form.option,
      feeAmount: feeAmountNum,
      organizationUUID: orgUUID,
    };
    const done = () => {
      setShowModal(false);
      setEditingId(null);
      setForm({ name: "", option: "amount", feeAmount: "" });
      load();
    };
    const fail = (err) => console.error(err);
    if (editingId != null) {
      updateCustomFee(editingId, payload)
        .then(done)
        .catch(fail)
        .finally(() => setSaving(false));
    } else {
      createCustomFee(payload)
        .then(done)
        .catch(fail)
        .finally(() => setSaving(false));
    }
  };

  const handleDelete = (row) => {
    const a = row.attributes ?? row;
    if (
      !window.confirm(
        `Delete fee “${a.name ?? "this fee"}”? This cannot be undone.`,
      )
    ) {
      return;
    }
    deleteCustomFee(row.id)
      .then(() => load())
      .catch((err) => console.error(err));
  };

  const footerNote = (
    <div className="custom-fees-panel__footer">
      <span className="custom-fees-panel__info-icon" aria-hidden>
        i
      </span>
      <p className="custom-fees-panel__footer-text">
        Custom fees are flat dollar amounts. 100% of fee revenue goes to the
        client.
      </p>
    </div>
  );

  const renderFeesTable = () => (
    <div className="custom-fees-list-section">
      <div className="buyer-types-custom">
        <div className="buyer-types-panel__toolbar buyer-types-panel__toolbar--custom">
          <h2 className="buyer-types-section-heading">Fees</h2>
          <Button type="button" variant="primary" onClick={openAddModal}>
            + Add fee
          </Button>
        </div>
        <div className="buyer-types-table-wrap custom-fees-table-wrap">
          <table className="buyer-types-table">
            <thead>
              <tr>
                <th scope="col">Fee name</th>
                <th scope="col">Amount</th>
                <th
                  scope="col"
                  className="buyer-types-table__actions"
                  aria-label="Actions"
                />
              </tr>
            </thead>
            <tbody>
              {sortedFees.map((row) => {
                const a = row.attributes ?? row;
                return (
                  <tr key={row.id}>
                    <td className="buyer-types-table__name">{a.name}</td>
                    <td className="custom-fees-table__amount">
                      {formatFeeAmount(a)}
                    </td>
                    <td className="buyer-types-table__actions">
                      <button
                        type="button"
                        className="buyer-types-table__icon-btn"
                        onClick={() => openEditModal(row)}
                        aria-label={`Edit ${a.name ?? "fee"}`}
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        className="buyer-types-table__icon-btn"
                        onClick={() => handleDelete(row)}
                        aria-label={`Delete ${a.name ?? "fee"}`}
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {footerNote}
    </div>
  );

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />;
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />;
  }

  if (isLoading) {
    return <PageLoadingContainer />;
  }

  return (
    <>
      <section className="wrapper" data-testid="custom-fees-section">
        <header className="section-header">
          <div className="section-heading section-heading--secondary">
            <h1>Custom fees</h1>
          </div>
          <p className="section-header-desc">
            Manage fees that apply across your offers, packages, and billing
          </p>
        </header>

        {fees.length === 0 ? (
          <>
            <Card body className="card--sm custom-fees-empty">
              <div className="custom-fees-empty__inner">
                <div className="custom-fees-empty__icon" aria-hidden>
                  i
                </div>
                <p className="custom-fees-empty__message">
                  No custom fees have been added yet
                </p>
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="px-4 rounded-pill custom-fees-empty__cta"
                  onClick={openAddModal}
                >
                  + Add fee
                </Button>
              </div>
            </Card>
            {footerNote}
          </>
        ) : isLoadingData ? (
          <LoadingContainer />
        ) : (
          renderFeesTable()
        )}
      </section>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        className="custom-fees-modal"
        contentClassName="custom-fees-modal__content"
        dialogClassName="custom-fees-modal__dialog"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="custom-fees-modal__header">
            <Modal.Title as="h2" className="custom-fees-modal__title">
              {editingId != null ? "Edit fee" : "Add fees"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="custom-fees-modal__body">
            <Form.Group
              className="custom-fees-modal__group"
              controlId="custom-fee-name"
            >
              <Form.Label className="custom-fees-modal__label">
                Fee name
              </Form.Label>
              <Form.Control
                className="custom-fees-modal__control"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Type fee name"
                required
                minLength={3}
              />
            </Form.Group>
            <Row className="custom-fees-modal__row g-3">
              {/* <Col sm={6}>
                <Form.Group controlId="custom-fee-option">
                  <Form.Label className="custom-fees-modal__label">
                    Options
                  </Form.Label>
                  <Form.Select
                    className="custom-fees-modal__control"
                    value={form.option}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, option: e.target.value }))
                    }
                    disabled={true}
                  >
                    <option value="amount">$ Amount</option>
                    <option value="percent">% Percent</option>
                  </Form.Select>
                </Form.Group>
              </Col> */}
              <Col sm={12}>
                <Form.Group controlId="custom-fee-amount">
                  <Form.Label className="custom-fees-modal__label">
                    Fee amount
                  </Form.Label>
                  <InputGroup className="custom-fees-modal__amount-group">
                    {form.option === "amount" ? (
                      <InputGroup.Text className="custom-fees-modal__prefix custom-fees-modal__prefix--start">
                        $
                      </InputGroup.Text>
                    ) : null}
                    <Form.Control
                      className={`custom-fees-modal__control ${
                        form.option === "amount"
                          ? "custom-fees-modal__control--with-prefix"
                          : "custom-fees-modal__control--with-suffix"
                      }`}
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      placeholder="0.00"
                      value={form.feeAmount}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, feeAmount: e.target.value }))
                      }
                      required
                    />
                    {form.option === "percent" ? (
                      <InputGroup.Text className="custom-fees-modal__prefix custom-fees-modal__prefix--end">
                        %
                      </InputGroup.Text>
                    ) : null}
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Button
              type="submit"
              className="custom-fees-modal__submit"
              disabled={saving || !canSubmitFee}
            >
              {saving
                ? editingId != null
                  ? "Saving…"
                  : "Adding…"
                : editingId != null
                  ? "Save changes"
                  : "Add fee"}
            </Button>
          </Modal.Body>
        </Form>
      </Modal>
    </>
  );
}
